import {
  Text,
  Flex,
  Box,
  Spinner,
  Table,
  Thead,
  Tr,
  Th,
  Tbody,
  Td
} from '@chakra-ui/react';
import { ClearButton, Menu, MenuButton, MenuList } from '@morressier/alembic';
import { useParams } from 'react-router-dom';
import { faBars, faUserPlus } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { fetchWorkspaceRoles } from 'src/api/queries';
import { useQuery } from '@tanstack/react-query';
import { useWorkspaceData } from 'src/contexts/WorkspaceProvider';
import ProtectedActionButton from 'src/components/ProtectedAction/Button';
import SubmissionLoader from 'src/pages/Organization/Workspace/Submissions/SubmissionLoader';
import { useNavigate } from 'react-router-dom';
import Footer from '../../../../../components/Footer';
import UpdateRole from './update';
import DeleteRole from './Delete';

const Roles = () => {
  const navigate = useNavigate();

  const { shortname = '' } = useParams();
  const { workspace, isLoading } = useWorkspaceData();

  const workspaceId = workspace.id;

  const queryKey = ['workspace-roles', { workspaceId }];
  const {
    data,
    isLoading: isLoadingRoles,
    isError: isRolesError
  } = useQuery({
    queryKey,
    queryFn: () => (workspaceId ? fetchWorkspaceRoles(workspaceId) : null),
    retry: 0
  });

  const roleConfigs = data?.roleConfigs ?? {};
  const hasRoles = Object.keys(roleConfigs).length > 0;

  if (isLoading) return <Spinner />;
  if (!workspace) return <div>Workspace not found</div>;

  return (
    <>
      <Box id="scrollable-wrapper" overflowY="auto" height="100%">
        <Box height="100%">
          <Box padding={{ base: 2, md: 5 }} minHeight="100%">
            <Flex
              alignItems={{ base: 'flex-start', md: 'center' }}
              flexDirection={{ base: 'column', md: 'row' }}
              gap={4}
              width="100%"
              justifyContent={'space-between'}
            >
              <Flex alignItems="center" gap={4}>
                <Text textStyle="headingSection">Roles</Text>
              </Flex>
              <Box marginStart="auto">
                {workspaceId && (
                  <ProtectedActionButton
                    relation="role_manager"
                    entityType="workspace"
                    entityId={workspaceId}
                    size="context"
                    variant="primary"
                    as="button"
                    iconLeft={<FontAwesomeIcon icon={faUserPlus} />}
                    onClick={() => {
                      navigate(
                        `/app/workspace/${shortname}/manage/roles/create`
                      );
                    }}
                    tooltipLabel={'This action is restricted to System Admins.'}
                  >
                    Create Role
                  </ProtectedActionButton>
                )}
              </Box>
            </Flex>

            <Box width="100%" mt={4} overflowX="auto">
              {isLoadingRoles ? (
                <SubmissionLoader
                  title="Loading Roles"
                  subTitle="We are retrieving the roles for this workspace. This should only take a moment."
                  bgColor="neutralSecondaryFill"
                />
              ) : isRolesError ? (
                <SubmissionLoader
                  title="Failed to Load Roles"
                  subTitle="There was an error loading the roles. Please try again later."
                  bgColor="errorPrimaryFill"
                />
              ) : hasRoles ? (
                <Table>
                  <Thead>
                    <Tr
                      data-testId="role-table-header"
                      gap={4}
                      width={'full'}
                      backgroundColor={'white'}
                      sx={{
                        '& th': {
                          textTransform: 'none',
                          color: 'black'
                        }
                      }}
                    >
                      <Th flex="1" textAlign="left">
                        <Text textStyle="headingItem" whiteSpace="nowrap">
                          Name
                        </Text>
                      </Th>
                      <Th flex="1" textAlign="left">
                        <Text textStyle="headingItem" whiteSpace="nowrap">
                          Display Name
                        </Text>
                      </Th>
                      <Th flex="1" textAlign="right">
                        <Text textStyle="headingItem" whiteSpace="nowrap">
                          Actions
                        </Text>
                      </Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {Object.keys(roleConfigs).map(config => (
                      <Tr
                        key={config}
                        data-testId="roles-table-row"
                        gap={4}
                        background="white"
                      >
                        <Td textStyle="headingLine" flex="1" textAlign="left">
                          <ClearButton ariaLabel={`view ${config} role`}>
                            {config}
                          </ClearButton>
                        </Td>
                        <Td textStyle="headingLine" flex="1" textAlign="left">
                          {data?.roleDisplayNames?.[config] ?? config}
                        </Td>
                        <Td textStyle="headingLine" flex="1" textAlign="right">
                          <Menu ariaLabel="member actions menu">
                            {workspaceId && (
                              <ProtectedActionButton
                                relation="role_manager"
                                entityType="workspace"
                                entityId={workspaceId}
                                size="context"
                                variant="primary"
                                shouldWrapChildren
                                as="text"
                                tooltipLabel={
                                  'This action is restricted to System Admins.'
                                }
                              >
                                {' '}
                                <MenuButton>
                                  <FontAwesomeIcon size="lg" icon={faBars} />{' '}
                                </MenuButton>
                              </ProtectedActionButton>
                            )}

                            <MenuList>
                              <UpdateRole
                                name={config}
                                displayName={
                                  data?.roleDisplayNames?.[config] ?? config
                                }
                                permission={roleConfigs[config] ?? {}}
                              ></UpdateRole>
                              <DeleteRole
                                name={config}
                                displayName={
                                  data?.roleDisplayNames?.[config] ?? config
                                }
                                workspaceId={workspaceId}
                              />
                            </MenuList>
                          </Menu>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              ) : (
                <SubmissionLoader
                  title="No roles yet"
                  subTitle='No roles have been created yet. Click "Create Role" to set up your first role.'
                  bgColor="neutralSecondaryFill"
                />
              )}
            </Box>
          </Box>
          <Footer />
        </Box>
      </Box>
    </>
  );
};

export default Roles;
